/*!
 * This module contains various server utility and helper functions.
 * Note that it automatically exports functions from utilities.client.js
 *
 * Author: Jan Curn (jan@apify.com)
 * Copyright(c) 2015 Apify. All rights reserved.
 *
 */

import crypto from 'node:crypto';

import type { Log } from '@apify/log';
import log, { LoggerJson, LogLevel } from '@apify/log';

/**
 * Generates a random cryptographically strong string consisting of 17 alphanumeric characters.
 * This string is similar to MongoDB ObjectIds generated by Meteor.
 */
export function cryptoRandomObjectId(length = 17): string {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCEDFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const bytes = crypto.randomBytes(length);
    let str = '';
    // eslint-disable-next-line
    for (let i = bytes.length - 1; i >= 0; i--) {
        str += chars[(bytes[i] | 0) % chars.length];
    }
    return str;
}

/**
 * Generates unique, deterministic record ID from the provided key with given length (defaults to 17).
 */
export function deterministicUniqueId(key: string, length = 17): string {
    return crypto
        .createHash('sha256')
        .update(key)
        .digest('base64')
        .replace(/(\+|\/|=)/g, 'x')
        .substr(0, length);
}

/**
 * Returns a random integer between 0 and max (excluded, unless it is also 0).
 * @param maxExcluded - exclusive upper bound; the returned value is always lower than this
 */
export function getRandomInt(maxExcluded: number) {
    maxExcluded = Math.floor(maxExcluded);
    return Math.floor(Math.random() * maxExcluded);
}

/**
 * If 'date' is a String, this function converts and returns it as a Date object.
 * Otherwise, the function returns the original 'date' argument.
 * This function is useful to convert dates transfered via JSON which doesn't natively support dates.
 */
export function parseDateFromJson(date: string | Date) {
    if (typeof date === 'string') {
        return new Date(Date.parse(date));
    }
    return date;
}

/**
 * Returns a Promise object that will wait a specific number of milliseconds.
 * @param millis - time to wait in milliseconds; if not larger than zero, the promise resolves immediately
 */
export async function delayPromise(millis: number): Promise<void> {
    return new Promise((resolve) => {
        if (millis > 0) {
            setTimeout(() => resolve(), millis);
        } else {
            resolve();
        }
    });
}

/**
 * Removes an element from an array.
 */
export function removeFromArray<T>(array: T[], element: T) {
    const index = array.indexOf(element);
    if (index >= 0) {
        array.splice(index, 1);
        return true;
    }
    return false;
}

interface RequestLike {
    url: string;
}

interface ResponseLike {
    status: (code: number) => void;
    send: (payload: string) => void;
    headersSent?: boolean;
}

/**
 * A default route for HTTP 404 error page for API endpoints.
 */
export function http404Route(req: RequestLike, res: ResponseLike) {
    res.status(404);
    res.send('Page not found');
}

/**
 * Default error handler of Express API endpoints.
 */
export function expressErrorHandler(
    err: Error,
    req: RequestLike,
    res: ResponseLike,
    next: (...a: unknown[]) => unknown,
) {
    log.warning('Client HTTP request failed', { url: req.url, errMsg: err.message });
    if (res.headersSent) {
        next(err);
        return;
    }
    res.status(505);
    res.send('Internal server error');
}

export type BetterIntervalID = { _betterClearInterval: () => void };

/**
 * Similar to setInterval() but with two important differences:
 * First, it assumes the function is asynchronous and only schedules its next invocation AFTER the asynchronous function finished.
 * Second, it invokes the function immediately.
 * @param func Function to be periodically executed.
 * For backwards compatibility reasons, it is passed a callback as its first argument during invocation, however that callback has no effect.
 * @param delay The number of milliseconds to wait to next invocation of the function after the current invocation finishes.
 * @returns Object that can be passed to betterClearInterval()
 */
export function betterSetInterval(
    func: ((a: (...args: unknown[]) => unknown) => void) | ((...args: unknown[]) => unknown),
    delay: number,
): BetterIntervalID {
    let scheduleNextRun: () => void;
    let timeoutId: NodeJS.Timeout;
    let isRunning = true;

    const funcWrapper = function () {
        // Historically, the function was passed a callback that it needed to call to signal it was done.
        // We keep passing this callback for backwards compatibility, but it has no effect anymore.
        void new Promise((resolve) => {
            resolve(func(() => undefined));
        }).finally(scheduleNextRun);
    };
    scheduleNextRun = function () {
        if (isRunning) timeoutId = setTimeout(funcWrapper, delay);
    };
    funcWrapper();

    return {
        _betterClearInterval() {
            isRunning = false;
            clearTimeout(timeoutId);
        },
    };
}

export function betterClearInterval(intervalID: BetterIntervalID) {
    // eslint-disable-next-line no-underscore-dangle
    if (intervalID && intervalID._betterClearInterval) {
        try {
            // eslint-disable-next-line no-underscore-dangle
            intervalID._betterClearInterval();
        } catch (e) {
            log.exception(e as Error, '_betterClearInterval() threw an exception!?');
        }
    }
}

/**
 * Escapes a string so that it can be used in regular expression (e.g. converts "myfile.*" to "myfile\\.\\*").
 */
export function escapeRegExp(str: string): string {
    // code taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * String left pad
 */
export function leftpad(str: string, len: number, ch: string | number = ' ') {
    // code inspired by https://www.theregister.co.uk/2016/03/23/npm_left_pad_chaos/
    str = String(str);
    let i = -1;

    if (!ch && ch !== 0) ch = ' ';

    len -= str.length;

    while (++i < len) {
        str = ch + str;
    }

    return str;
}

/**
 * Computes weighted average of 2 values.
 */
export function weightedAverage(val1: number, weight1: number, val2: number, weight2: number) {
    return (val1 * weight1 + val2 * weight2) / (weight1 + weight2);
}

/**
 * Executes array of promises in sequence and then returns array where Nth item is result of Nth promise.
 */
export async function sequentializePromises<T>(promises: (Promise<T> | (() => Promise<T>))[]) {
    if (!promises.length) return [];
    const results: T[] = [];

    for (const promiseOrFunc of promises) {
        const promise = promiseOrFunc instanceof Function ? promiseOrFunc() : promiseOrFunc;
        results.push(await promise);
    }

    return results;
}

/**
 * Helper function for validation if parameter is an instance of given prototype or multiple prototypes.
 */
export function checkParamPrototypeOrThrow(
    paramVal: any,
    paramName: string,
    prototypes: any,
    prototypeName: string,
    isOptional = false,
) {
    if (isOptional && (paramVal === undefined || paramVal === null)) return;

    const hasCorrectPrototype =
        prototypes instanceof Array
            ? prototypes.some((prototype) => paramVal instanceof prototype)
            : paramVal instanceof prototypes;

    if (!hasCorrectPrototype) throw new Error(`Parameter "${paramName}" must be an instance of ${prototypeName}`);
}

interface Server {
    removeListener(event: string, cb: (...params: any[]) => any): unknown;
    on(event: string, cb: (...params: any[]) => any): unknown;
    listen(port: number): unknown;
}

/**
 * Starts listening at a port specified in the constructor.
 * Unfortunately server.listen() is not a normal function that fails on error, so we need this trickery.
 * Returns a function that calls `server.listen(port)` and resolves once server starts listening.
 *
 * Usage: `promisifyServerListen(server)(1234)`;
 */
export function promisifyServerListen<T extends Server>(server: T) {
    return async (port: number) => {
        return new Promise<void>((resolve, reject) => {
            function removeListeners() {
                server.removeListener('error', onError);
                server.removeListener('listening', onListening);
            }
            function onError(err: Error) {
                removeListeners();
                reject(err);
            }
            function onListening() {
                removeListeners();
                resolve();
            }

            server.on('error', onError);
            server.on('listening', onListening);
            server.listen(port);
        });
    };
}

export function configureLogger(givenLog: Log, isProduction?: boolean) {
    if (isProduction) {
        givenLog.setOptions({
            level: LogLevel.INFO,
            logger: new LoggerJson(),
        });
    } else {
        givenLog.setOptions({ level: LogLevel.DEBUG });
    }
}

/**
 * Wraps given promise with timeout.
 */
export async function timeoutPromise<T>(
    promise: Promise<T>,
    timeoutMillis: number,
    errorMessage = 'Promise has timed-out',
) {
    return new Promise((resolve, reject) => {
        let timeout: number;
        let hasFulfilled = false;

        const callback = (err: Error | null, result?: T) => {
            if (hasFulfilled) return;
            clearTimeout(timeout);
            hasFulfilled = true;
            if (err) {
                reject(err);
                return;
            }
            resolve(result);
        };

        promise.then<void, void>((result: T) => callback(null, result), callback);
        timeout = setTimeout(() => callback(new Error(errorMessage)), timeoutMillis) as unknown as number;
    });
}

/**
 * Removes the leading ^ and trailing $ from regex
 */
export function createInjectableRegExp(regex: RegExp) {
    return new RegExp(regex.source.replace(/^\^|\$$/g, ''));
}
